import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { XMarkIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { FilterGroup } from '../types'
import toast from 'react-hot-toast'

interface SaveSuppressionListModalProps {
  suppressionListId: string
  filter: FilterGroup | null
  rowCount: number
  onClose: () => void
}

export default function SaveSuppressionListModal({ 
  suppressionListId, 
  filter, 
  rowCount, 
  onClose 
}: SaveSuppressionListModalProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [listType, setListType] = useState<'static' | 'dynamic'>('static')
  
  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        name,
        description: description || undefined,
        list_type: listType,
        filter_definition: filter || undefined,
      }
      
      const res = await api.post(
        `/suppression-lists/${suppressionListId}/saved-lists`,
        payload
      )
      
      return res.data
    },
    onSuccess: () => {
      toast.success('Saved suppression list created successfully!')
      queryClient.invalidateQueries({ queryKey: ['saved-suppression-lists', suppressionListId] })
      onClose()
    },
    onError: () => {
      toast.error('Failed to create saved suppression list')
    },
  })
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Please enter a list name')
      return
    }
    if (listType === 'dynamic' && !filter) {
      toast.error('Dynamic lists require a filter. Please add a filter first.')
      return
    }
    createMutation.mutate()
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Save as Suppression List</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              List Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="e.g., Gmail Bounces"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Optional description..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              List Type
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="listType"
                  value="static"
                  checked={listType === 'static'}
                  onChange={() => setListType('static')}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Static List</span>
                    <span className="text-lg">📸</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {filter 
                      ? `Snapshot of ${rowCount.toLocaleString()} current rows matching your filter`
                      : `Add all ${rowCount.toLocaleString()} rows to this list`
                    }
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Future changes to parent list won't affect this saved list
                  </div>
                </div>
              </label>
              
              <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${!filter ? 'opacity-50' : 'hover:bg-gray-50'}`}>
                <input
                  type="radio"
                  name="listType"
                  value="dynamic"
                  checked={listType === 'dynamic'}
                  onChange={() => setListType('dynamic')}
                  disabled={!filter}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Dynamic List</span>
                    <span className="text-lg">🔄</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    Auto-updates when parent data changes
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {filter 
                      ? 'Filter is re-executed every time you view this list'
                      : 'Requires a filter to be applied'
                    }
                  </div>
                </div>
              </label>
            </div>
          </div>
          
          {filter && (
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="text-xs font-medium text-gray-700 mb-1">Current Filter:</div>
              <div className="text-xs text-gray-600 font-mono">
                {JSON.stringify(filter, null, 2)}
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Saved List'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
